import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@verifime/api-definition';
import { reportPageActionToNewRelic } from '@verifime/utils';
import { useEffect, useMemo } from 'react';
import { failureReason } from '../../WorkflowMetadataContext/workflowMetadataReporting';
import { FRESH_READ } from './freshRead';

/**
 * The New Relic page action for a failed read. The browser agent does not collect console output in
 * this deployment, so this is what engineering sees. It carries `source` (a constant label) and
 * `status` (a reason string): never an id, a token, the error object or anything from a response.
 */
export const SHARED_IDENTITY_IDS_READ_FAILED_PAGE_ACTION = 'shared-identity-ids-read-failed';

/**
 * What the reads came to.
 *
 * - `loading`: a read is still running, or has not run since this mount. This wins over `error`
 *   while any read is pending, so an error shows once every read has run.
 * - `loaded`: every identity was read. `ids` holds the ones already shared with the client.
 * - `error`: no client to ask about, or a read failed once all had run. `ids` is empty and means
 *   nothing: a partial answer would hide a shared identity, and the screen could then arrive with
 *   it chosen.
 */
export type TSharedIdentityIds = {
  status: 'loading' | 'loaded' | 'error';
  ids: ReadonlySet<string>;
};

/** The first part of the key of each identity's permissions read, to refresh them all at once. */
export const PERMISSIONS_OF_CUSTOMER_KEY = 'permissions-of-customer';

/** No identity is shared with the client. */
export const NO_SHARED_IDENTITIES: ReadonlySet<string> = new Set();

/**
 * Reads the permissions of one identity (`GET /v1/permission?customerId=`). A failure is rethrown
 * as its reason alone, so React Query never holds, and never logs, the request or the response body.
 */
async function readPermissionsOf(customerId: string) {
  try {
    return await api.getV1permission({ queries: { customerId } });
  } catch (error) {
    throw new Error(failureReason(error));
  }
}

function reportFailure(reason: string) {
  console.error('Shared identities could not be read', { reason });
  reportPageActionToNewRelic(SHARED_IDENTITY_IDS_READ_FAILED_PAGE_ACTION, {
    source: 'shared-identity-ids',
    status: reason,
  });
}

const reportFailedRead = (error: unknown) =>
  reportFailure(error instanceof Error ? error.message : 'unexpected-error');

/** The reason reported when there is no client to ask about. */
const NO_CLIENT_REASON = 'no-client';

type TReadState = Pick<UseQueryResult, 'isFetchedAfterMount' | 'isError'>;

/**
 * The answer of all the reads. No client is an error, not "none shared", unless the client is
 * still being read, when it is loading. Otherwise it is loading
 * until every read has run since this mount, whether or not one of them has already failed, and
 * then an error if any failed.
 */
function readsStatusOf(
  hasClient: boolean,
  isClientPending: boolean,
  reads: readonly TReadState[],
): TSharedIdentityIds['status'] {
  if (!hasClient) {
    return isClientPending ? 'loading' : 'error';
  }
  if (reads.some((read) => !read.isFetchedAfterMount)) {
    return 'loading';
  }
  return reads.some((read) => read.isError) ? 'error' : 'loaded';
}

/** The fields of a permission that say whether it shares an identity with a client. */
export type TPermissionOfCustomer = { permissionStatus?: string; organisationId?: string };

/**
 * True when one of an identity's permissions is Active for this client. This client only: an Active
 * permission for another client does not count. Absent permissions (not read) are not shared.
 */
export const hasActivePermissionFor = (
  permissions: readonly TPermissionOfCustomer[] | undefined,
  organisationId: string,
): boolean =>
  !!permissions?.some(
    (permission) =>
      permission.permissionStatus === 'Active' && permission.organisationId === organisationId,
  );

/**
 * Which of the given wallet identities already hold an Active permission for this client. This
 * client only: an Active permission for another client does not count. This is the consents step's
 * rule for "already shared" in an ask-first journey (it checks any client otherwise). There is no
 * batch endpoint, so it is one read per identity.
 *
 * With no client nothing is read and the answer is an error: nothing can be said to be shared with
 * a client that is not there, and `none shared` would let a shared identity arrive chosen. While
 * the caller is still reading the client (`isClientPending`) the answer is loading, and the missing
 * client is not reported: it is not yet known to be missing.
 */
export function useSharedIdentityIds(
  organisationId: string | undefined,
  identityIds: string[],
  isClientPending = false,
): TSharedIdentityIds {
  const hasClient = !!organisationId;
  const reads = useQueries({
    queries: hasClient
      ? identityIds.map((identityId) => ({
          queryKey: [PERMISSIONS_OF_CUSTOMER_KEY, identityId],
          queryFn: () => readPermissionsOf(identityId),
          onError: reportFailedRead,
          ...FRESH_READ,
        }))
      : [],
  });

  const status = readsStatusOf(hasClient, isClientPending, reads);
  useEffect(() => {
    if (!hasClient && !isClientPending) {
      reportFailure(NO_CLIENT_REASON);
    }
  }, [hasClient, isClientPending]);

  const sharedKey =
    status === 'loaded'
      ? identityIds
          .filter((_, index) => hasActivePermissionFor(reads[index]?.data, organisationId))
          .join('|')
      : '';

  // One Set per answer, so a caller can depend on it without re-running on every render.
  return useMemo(
    () => ({
      status,
      ids: sharedKey ? new Set(sharedKey.split('|')) : NO_SHARED_IDENTITIES,
    }),
    [status, sharedKey],
  );
}
